import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSettings } from '@/features/settings';
import {
  startMeasurement,
  cancelMeasurement,
  selectMeasurementStatus,
  selectPendingValue,
  selectSelectedImageTools,
  type MeasurementStatus,
} from '@/state/slices/measurementSession';
import type { ViewerPopupMessage } from './useViewerPopup';

const VIEWER_POPUP_CHANNEL = 'viewer-popup';

interface UsePopupChannelOptions {
  /** Called when the main window requests a jump to an annotation's
   *  DICOM/frame. ViewerProvider sets this via a ref bridge. */
  goToAnnotationRef?: React.MutableRefObject<
    ((dicomUuid: string, frameNumber: number) => void) | null
  >;
  /** Called when the main window asks to show a set of clips (AP-965). */
  openDicomsRef?: React.MutableRefObject<
    ((dicomUuids: string[]) => void) | null
  >;
}

/**
 * Manages the BroadcastChannel from the popup window side.
 * Listens for study changes from the main window and navigates accordingly.
 * Notifies the main window on mount and before closing.
 * Also closes the popup when poppedOutViewer setting is toggled off.
 */
export function usePopupChannel(options: UsePopupChannelOptions = {}) {
  const { goToAnnotationRef, openDicomsRef } = options;
  const navigate = useNavigate();
  const { studyUuid } = useParams<{ studyUuid: string }>();
  const { settings } = useSettings();
  const dispatch = useDispatch();
  const measurementStatus = useSelector(selectMeasurementStatus);
  const pendingValue = useSelector(selectPendingValue);
  const imageTools = useSelector(selectSelectedImageTools);
  const imageToolsRef = useRef(imageTools);
  const prevStatusRef = useRef<MeasurementStatus>(measurementStatus);
  const studyUuidRef = useRef(studyUuid);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const [isIdle, setIsIdle] = useState(false);

  useEffect(() => {
    studyUuidRef.current = studyUuid;
  }, [studyUuid]);

  useEffect(() => {
    const channel = new BroadcastChannel(VIEWER_POPUP_CHANNEL);
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent<ViewerPopupMessage>) => {
      const msg = event.data;
      switch (msg.type) {
        case 'study-changed':
          setIsIdle(false);
          navigate(`/studies/${msg.studyUuid}/viewer`, { replace: true });
          break;
        case 'study-closed':
          setIsIdle(true);
          break;
        case 'close-popup':
          window.close();
          break;
        case 'ping':
          channel.postMessage({
            type: 'pong',
            studyUuid: studyUuidRef.current!,
          });
          channel.postMessage({
            type: 'selected-image',
            tools: imageToolsRef.current,
          });
          break;
        case 'start-measurement':
          dispatch(startMeasurement(msg.measurement));
          break;
        case 'cancel-measurement':
          // Seen as idle already, so it isn't relayed back to the main window.
          prevStatusRef.current = 'idle';
          dispatch(cancelMeasurement());
          break;
        case 'go-to-annotation':
          goToAnnotationRef?.current?.(msg.dicomUuid, msg.frameNumber);
          break;
        case 'open-dicoms':
          openDicomsRef?.current?.(msg.dicomUuids);
          break;
        default:
          break;
      }
    };

    // Announce that popup is ready
    channel.postMessage({
      type: 'popup-ready',
      studyUuid: studyUuidRef.current!,
    });

    // Notify main window when popup is closed via OS close button
    const handleBeforeUnload = () => {
      channel.postMessage({ type: 'popup-closing' });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      channel.close();
      channelRef.current = null;
    };
  }, [navigate, dispatch, goToAnnotationRef, openDicomsRef]);

  // Close popup when poppedOutViewer setting is toggled off
  // (e.g., from the Settings page while StudyDetail isn't mounted)
  const prevPoppedOut = useRef(settings.poppedOutViewer);
  useEffect(() => {
    if (prevPoppedOut.current && !settings.poppedOutViewer) {
      window.close();
    }
    prevPoppedOut.current = settings.poppedOutViewer;
  }, [settings.poppedOutViewer]);

  // Relay measurement completion back to main window
  useEffect(() => {
    if (measurementStatus === 'completed' && pendingValue !== null) {
      channelRef.current?.postMessage({
        type: 'complete-measurement',
        value: pendingValue,
      });
    }
  }, [measurementStatus, pendingValue]);

  // A disarm here (Escape, or an image its tool can't use) disarms the row.
  useEffect(() => {
    if (prevStatusRef.current === 'awaiting' && measurementStatus === 'idle') {
      channelRef.current?.postMessage({ type: 'cancel-measurement' });
    }
    prevStatusRef.current = measurementStatus;
  }, [measurementStatus]);

  // The selected image gates the main window's worksheet rows.
  useEffect(() => {
    imageToolsRef.current = imageTools;
    channelRef.current?.postMessage({
      type: 'selected-image',
      tools: imageTools,
    });
  }, [imageTools]);

  const handleClose = useCallback(() => {
    window.close();
  }, []);

  return { handleClose, isIdle };
}
